import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { auth, card } from '../RealTradePage';

// ─────────────────────────────────────────────
// [RT_APTVOL_V1] 단지분석 — 선택 단지의 평형별 월별 거래건수
//
// [화면]
//   · 막대 하나 = 그 달 단지 전체 거래건수, 막대 조각 = 평형
//   · [면적타입별] 76㎡·84㎡ 처럼 실제 타입 (위 가격추이 그래프와 같은 기준 area_type)
//     [평형구간별] 거래량 탭과 같은 6구간 (다른 단지·지역과 비교할 때)
//
// [연관]
//   - 백엔드 GET /api/real-trades/volume?group=area|bucket&lawd_cd&umd_nm&jibun
//     ([RT_VOLUME_V1] 라우트를 그대로 사용, area 는 [RT_APTVOL_V1] 에서 추가)
//   - AptTrend.jsx 가 detail(선택 단지)의 lawd_cd·umd_nm·jibun 을 내려준다
//   - 해제거래 제외 (다른 탭과 같은 원칙)
// ─────────────────────────────────────────────

const COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4',
                '#ec4899', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#a855f7'];

const chip = (on) => ({
  padding: '4px 12px', fontSize: 12, cursor: 'pointer', borderRadius: 14,
  border: on ? '1px solid #3b82f6' : '1px solid #cbd5e1',
  background: on ? '#3b82f6' : '#fff', color: on ? '#fff' : '#475569',
  fontWeight: on ? 600 : 400,
});

// 마우스를 올렸을 때: 그 달 합계 + 평형별 건수
function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload[0]?.payload?.total || 0;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                  padding: '8px 10px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label} · 합계 {total}건</div>
      {payload.filter(p => p.value > 0).map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}건</div>
      ))}
    </div>
  );
}

export default function AptBucketVolume({ lawdCd, umdNm, jibun, from, to, propType }) {
  const [mode, setMode] = useState('area');   // area=면적타입별, bucket=평형구간별
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    // 단지가 특정되지 않았거나 기간 입력 중이면 조회하지 않는다
    if (!lawdCd || !umdNm || jibun === undefined || jibun === null) { setRes(null); return; }
    if (!/^\d{6}$/.test(from || '') || !/^\d{6}$/.test(to || '')) return;

    let alive = true;   // 단지를 빨리 바꿔 누를 때 늦게 온 옛 응답이 덮어쓰지 않게
    setLoading(true); setErr('');
    const qs = new URLSearchParams({
      group: mode, lawd_cd: lawdCd, umd_nm: umdNm, jibun: String(jibun),
      from, to, prop_type: propType,
    });
    axios.get(`/api/real-trades/volume?${qs.toString()}`, auth())
      .then(r => { if (alive) setRes(r.data); })
      .catch(e => { if (alive) { setErr(e.response?.data?.error || e.message); setRes(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lawdCd, umdNm, jibun, from, to, propType, mode]);

  if (!lawdCd || !umdNm) return null;

  const data = (res?.rows || []).map(r => ({ ...r, x: `${r.ym.slice(2, 4)}.${r.ym.slice(4, 6)}` }));
  const nMonths = res?.months?.length || 1;

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <b>📶 평형별 월별 거래건수</b>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setMode('area')}   style={chip(mode === 'area')}>면적타입별</button>
          <button onClick={() => setMode('bucket')} style={chip(mode === 'bucket')}>평형구간별</button>
        </div>
      </div>

      {err && <div style={{ padding: 8, color: '#991b1b', fontSize: 13 }}>⚠️ {err}</div>}
      {loading && <div style={{ padding: 8, color: '#3b82f6', fontSize: 13 }}>조회 중…</div>}

      {res && !loading && (
        res.total === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            이 기간에는 거래가 없습니다
          </div>
        ) : (
          <>
            {/* 평형별 합계 — 색 표시 + 건수·비중·월평균 */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, fontSize: 12 }}>
              <span style={{ padding: '4px 10px', background: '#f1f5f9', borderRadius: 4 }}>
                합계 <b>{res.total.toLocaleString()}</b>건 · 월평균 <b>{(res.total / nMonths).toFixed(1)}</b>건
              </span>
              {res.series.map((s, i) => (
                <span key={s.id} style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                  <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, marginRight: 5,
                                 background: COLORS[i % COLORS.length] }} />
                  {s.label} <b>{s.total}</b>건 ({(s.total / res.total * 100).toFixed(0)}%)
                </span>
              ))}
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip content={<Tip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {res.series.map((s, i) => (
                  <Bar key={s.id} dataKey={s.id} name={s.label} stackId="v"
                       fill={COLORS[i % COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              해제거래 제외 · 원본 거래 기준 · 평은 전용면적 기준
            </div>
          </>
        )
      )}
    </div>
  );
}
